/* eslint-disable */
import React, { useMemo } from "react";
import { Row, Col, Descriptions, Tag, Empty, Tooltip } from "antd";
import {
  useLastLiftMessage,
  useLastBankMessage,
  useAllLiftMessages,
} from "../socketMessageState/BankMessagesContext";
// REDUX
import { useSelector } from "react-redux";
import { faPlugCircleCheck } from "@fortawesome/free-solid-svg-icons";
import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { LiftInterface } from "../../../interfaces/Lift.interface";
import { BLANK_SOCKET_MESSAGE } from "../../../constants/monitoring";
import { extractSuffix } from "../../../utils/stateMapHelpers";
import { uuidToString, stringToUuidT, EMPTY_UUID } from "../../../utils/uuid";
import { ElevatorUsage } from "../../../flatbuffers/generated/usage/elevator-usage";
import { DeviceDirection } from "../../../types/DeviceDirection";
import { directionDisplayName } from "../../../flatbuffers/types/enumDisplayUtils";
import { serviceColorByName } from "../../../utils/serviceModeDisplay";
import DrawerRowDivider from "../../components/drawer/DrawerRowDivider";
import { RootState } from "../../../app/store";
import { getMessageTypeColor } from "../../../constants/iconDefinition";
import { EncodedMessageContent } from "../../../flatbuffers/generated/ln2/encoded-message-content";
import { BitMaskMessageContent } from "../../../flatbuffers/generated/ln2/bit-mask-message-content";
import { OneHotMessageContent } from "../../../flatbuffers/generated/ln2/one-hot-message-content";
import { textAlign, width } from "@mui/system";

function getDirectionColor(direction: DeviceDirection): string {
  if (direction === ElevatorUsage.Up) return "green";
  if (direction === ElevatorUsage.Down) return "red";
  return "default";
}

function getMessageCategoryColor(category: string): string {
  const normalizedCategory = category.trim().toUpperCase();

  if (normalizedCategory === "USAGE") {
    return getMessageTypeColor(
      EncodedMessageContent.OneHotMessage,
      OneHotMessageContent.Usage_UsageMessage,
    );
  }
  if (normalizedCategory === "TRAFFIC") {
    return getMessageTypeColor(
      EncodedMessageContent.BitMaskMessage,
      BitMaskMessageContent.Traffic_TrafficMessage,
    );
  }
  if (normalizedCategory === "CONTROL") {
    return getMessageTypeColor(
      EncodedMessageContent.BitMaskMessage,
      BitMaskMessageContent.Control_ControlMessage,
    );
  }
  if (normalizedCategory === "FAULT") {
    return getMessageTypeColor(
      EncodedMessageContent.BitMaskMessage,
      BitMaskMessageContent.Fault_FaultMessage,
    );
  }
  if (normalizedCategory === "STATUS") {
    return getMessageTypeColor(
      EncodedMessageContent.BitMaskMessage,
      BitMaskMessageContent.Status_StatusMessage,
    );
  }

  return getMessageTypeColor(
    EncodedMessageContent.BitMaskMessage,
    BitMaskMessageContent.Status_StatusMessage,
  );
}

function StateCard({
  title,
  msg,
  color = "default",
  liftNameMap = {},
  isSingleLift = false,
}: {
  title: string;
  msg: SocketMessageInterface;
  color?: string;
  liftNameMap?: Record<string, string>;
  isSingleLift?: boolean;
}) {
  const { theme } = useSelector((state: RootState) => state.home);

  // Filter out Service:* entries — service is shown from service_map instead
  const displayMapEntries = Object.entries(msg.bitmask)
    .filter(([key]) => !extractSuffix(key).startsWith("Service:"))
    .sort(([a], [b]) => a.localeCompare(b));

  const nonFaultDisplayMapEntries = displayMapEntries.filter(([key]) => {
    const suffix = extractSuffix(key);
    const [messageType] = suffix.split(":");
    return messageType.trim().toUpperCase() !== "FAULT";
  });

  const faultDisplayMapEntries = displayMapEntries.filter(([key]) => {
    const suffix = extractSuffix(key);
    const [messageType] = suffix.split(":");
    return messageType.trim().toUpperCase() === "FAULT";
  });

  // Use service_map (single value per lift, no accumulation)
  const svcKey = `${uuidToString(msg.bankId)}:${uuidToString(msg.liftId) || ""}`;
  const currentService = msg.serviceMap[svcKey] || null;

  // Use position_map for position display
  const currentPosition = msg.positionMap[svcKey] ?? msg.position;

  // Collect all position_map entries for this message
  const positionEntries = Object.entries(msg.positionMap);
  const serviceEntries = Object.entries(msg.serviceMap);

  /** Resolve a compound key (bankId:liftId) to a lift name or fallback. */
  const resolveName = (compoundKey: string): string => {
    // Extract liftId from "bankId:liftId"
    const colonIdx = compoundKey.indexOf(":");
    if (colonIdx === -1) return compoundKey;
    const id = compoundKey.substring(colonIdx + 1);
    return liftNameMap[id] || id.substring(0, 8);
  };

  return (
<div className="monitoringView__detailsBox mb-3">
      <div
        className={`monitoringView__controlPanel monitoringView__controlPanel__${theme}`}
        style={{
          minHeight: "400px",
        }}
      >
      {/** **************************************************** Direction, Position, Doors **************************************************** */}
      <DrawerRowDivider icon={faPlugCircleCheck} title="Direction, Position, Doors" />
      <div className={` drawer__box__control__panel__${theme} mt-3`}>
        <Descriptions bordered size="small" column={2}>
          <Descriptions.Item label="Direction">
            <Tag
              className="generalStyles__width100px generalStyles__textCenter"
              color={getDirectionColor(msg.direction)}
            >
              {directionDisplayName(msg.direction)}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Position">
            <Tag className="generalStyles__width100px generalStyles__textCenter" color="cyan">
              {currentPosition}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Front Door">
            <Tag
              className="generalStyles__width100px generalStyles__textCenter"
              color={msg.doorStatus.frontOpen ? "green" : "red"}
            >
              {msg.doorStatus.frontOpen ? "Open" : "Closed"}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Rear Door">
            <Tag
              className="generalStyles__width100px generalStyles__textCenter"
              color={msg.doorStatus.rearOpen ? "green" : "red"}
            >
              {msg.doorStatus.rearOpen ? "Open" : "Closed"}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Service Mode">
            <Tag
              className="generalStyles__width100px generalStyles__textCenter"
              color={currentService ? serviceColorByName(currentService) : "default"}
            >
              {currentService ?? "None"}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Faults">
            <Tag
              className="generalStyles__width100px generalStyles__textCenter"
              color={msg.faults ? "red" : "green"}
            >
              {msg.faults ? "Active" : "None"}
            </Tag>
          </Descriptions.Item>
        </Descriptions>
      </div>

      {/** **************************************************** Position Map **************************************************** */}
      <DrawerRowDivider icon={faPlugCircleCheck} title="Position Map" />
      <div className={` drawer__box__control__panel__${theme} mt-3`}>
        {positionEntries.length > 0 && (
          <Descriptions bordered size="small" column={2} style={{ marginTop: 12 }}>
            {positionEntries.map(([key, val]) => (
              <Descriptions.Item key={key} label={isSingleLift ? "Position" : resolveName(key)}>
                <Tag className="generalStyles__width100px generalStyles__textCenter" color="cyan">
                  {String(val)}
                </Tag>
              </Descriptions.Item>
            ))}
          </Descriptions>
        )}
      </div>
      {/** **************************************************** Service Map **************************************************** */}
      <DrawerRowDivider icon={faPlugCircleCheck} title="Service Map" />
      <div className={` drawer__box__control__panel__${theme} mt-3`}>
        {serviceEntries.length > 0 && (
          <Descriptions bordered size="small" column={2} style={{ marginTop: 12 }}>
            {serviceEntries.map(([key, val]) => (
              <Descriptions.Item key={key} label={isSingleLift ? "Service" : resolveName(key)}>
                <Tag className="generalStyles__width100px generalStyles__textCenter" color={serviceColorByName(val)}>
                  {val}
                </Tag>
              </Descriptions.Item>
            ))}
          </Descriptions>
        )}
      </div>
      {/** **************************************************** Bitmask Map **************************************************** */}
      <DrawerRowDivider icon={faPlugCircleCheck} title="Bitmask Map" />
      <div className={` drawer__box__control__panel__${theme} mt-3`}>
        {displayMapEntries.length === 0 ? (
          <Empty description="No bitmask entries" style={{ marginTop: 12 }} />
        ) : (
          <>
            {nonFaultDisplayMapEntries.length > 0 && (
              <Descriptions bordered size="small" column={2} style={{ marginTop: 12 }}>
                {nonFaultDisplayMapEntries.map(([key, value]) => (
                  (() => {
                    const suffix = extractSuffix(key);
                    const [messageType, ...subTypeParts] = suffix.split(":");
                    const messageSubType = subTypeParts.join(":");
                    return (
                      <Descriptions.Item
                        key={key}
                        label={
                          <span>
                            <Tag style={{ width: "80px", textAlign: "center" }} color={getMessageCategoryColor(messageType)}>{messageType}</Tag>
                            {messageSubType ? `: ${messageSubType}` : ""}
                          </span>
                        }
                      >
                        <Tooltip title={suffix} className="generalStyles__hoverCursor">
                          <Tag
                            className="generalStyles__width50px generalStyles__textCenter generalStyles__textEllipsis"
                            color={value ? "green" : "red"}
                          >
                            {value ? "ON" : "OFF"}
                          </Tag>
                        </Tooltip>
                      </Descriptions.Item>
                    );
                  })()
                ))}
              </Descriptions>
            )}

            {faultDisplayMapEntries.length > 0 && (
              <Descriptions bordered size="small" column={2} style={{ marginTop: 12 }}>
                {faultDisplayMapEntries.map(([key, value]) => (
                  (() => {
                    const suffix = extractSuffix(key);
                    const [messageType, ...subTypeParts] = suffix.split(":");
                    const messageSubType = subTypeParts.join(":");
                    return (
                      <Descriptions.Item
                        key={key}
                        label={
                          <span>
                            <Tag style={{ width: "80px", textAlign: "center" }} color={getMessageCategoryColor(messageType)}>{messageType}</Tag>
                            {messageSubType ? `: ${messageSubType}` : ""}
                          </span>
                        }
                      >
                        <Tooltip title={suffix} className="generalStyles__hoverCursor">
                          <Tag
                            className="generalStyles__width50px generalStyles__textCenter generalStyles__textEllipsis"
                            color={value ? "green" : "red"}
                          >
                            {value ? "ON" : "OFF"}
                          </Tag>
                        </Tooltip>
                      </Descriptions.Item>
                    );
                  })()
                ))}
              </Descriptions>
            )}
          </>
        )}
      </div>
    </div>
    </div>
  );
}

function ControlPanelStateView({
  liftId = "",
  bankId = "",
  bankLifts = [],
}: {
  liftId?: string;
  bankId?: string;
  bankLifts?: LiftInterface[];
}) {
  const liftUuid = useMemo(() => (liftId ? stringToUuidT(liftId) : EMPTY_UUID), [liftId]);
  const bankUuid = useMemo(() => (bankId ? stringToUuidT(bankId) : EMPTY_UUID), [bankId]);
  const liftMsg: SocketMessageInterface | null = useLastLiftMessage(liftUuid) ?? null;
  const bankMsg: SocketMessageInterface | null = useLastBankMessage(bankUuid) ?? null;
  const allLiftMessages = useAllLiftMessages();

  const socketMsg: SocketMessageInterface = (liftId ? liftMsg : bankMsg) || BLANK_SOCKET_MESSAGE;

  // Build a name lookup from bankLifts for contextual labels
  const liftNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    bankLifts.forEach((lift) => {
      if (lift.id && lift.name) {
        map[lift.id] = lift.name;
      }
    });
    return map;
  }, [bankLifts]);

  const liftCards = useMemo(() => {
    if (!bankId || bankLifts.length === 0) return [];
    return bankLifts
      .map((lift) => ({
        lift,
        msg: allLiftMessages[lift.id] || null,
      }))
      .filter((entry) => entry.msg !== null);
  }, [bankId, bankLifts, allLiftMessages]);

  const isBankView = !!bankId && bankLifts.length > 0;

  if (!isBankView) {
    return (
      <StateCard
        title={liftId ? "Lift" : "Bank"}
        msg={socketMsg}
        color={liftId ? "purple" : "blue"}
        isSingleLift={!!liftId}
      />
    );
  }

  return (
    <Row >
      <Col xs={24} md={24}>
        <StateCard title="Bank" msg={socketMsg} color="blue" liftNameMap={liftNameMap} />
      </Col>
      {liftCards.map((entry) => (
        <Col key={entry.lift.id} xs={24} md={24}>
          <StateCard
            title={entry.lift.name || entry.lift.id.substring(0, 8)}
            msg={entry.msg as SocketMessageInterface}
            color="purple"
            liftNameMap={liftNameMap}
            isSingleLift
          />
        </Col>
      ))}
    </Row>
  );
}

export default ControlPanelStateView;
